import { useLingui } from "@lingui/react/macro";
import { FormControl, FormInput } from "@offstreetparking/design-system/forms";
import {
	type PaymentAttempt,
	decidePaymentRecovery,
} from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import type { PaymentFormError } from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import { Form } from "@offstreetparking/portal-flow/components/forms/Form";
import * as z from "zod/v4";
import { offstreetClient } from "~/services/offstreetApi";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { useSpreedly } from "../../hooks/useSpreedly";

const NUMBER_CONTAINER_ID = "spreedly-number";
const CVV_CONTAINER_ID = "spreedly-cvv";

// Shared styling for the secure-iframe containers so they match our text inputs.
const HOSTED_FIELD_CLASS = "h-9 rounded-md border border-input px-3 [&>iframe]:block [&>iframe]:h-full";

// Formats raw keystrokes into MM/YY as the user types (digits only, slash auto-inserted).
const formatExpiry = (value: string): string => {
	const digits = value.replace(/\D/g, "").slice(0, 4);
	return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// Parses "MM/YY" into the month + 4-digit year Spreedly expects; null only when not in MM/YY shape. Range
// and expiry validity are delegated to Spreedly so its (now user-friendly) field errors surface instead.
const parseExpiry = (value: string): { month: string; year: string } | null => {
	const match = /^(\d{2})\/(\d{2})$/.exec(value);
	const monthPart = match?.[1];
	const yearPart = match?.[2];
	if (!monthPart || !yearPart) {
		return null;
	}
	return { month: monthPart, year: String(2000 + Number(yearPart)) };
};

const createSpreedlySchema = ({ invalidCardDetailsMessage }: { invalidCardDetailsMessage: string }) =>
	z.object({
		firstName: z.string().min(1),
		lastName: z.string().min(1),
		expiry: z.string().refine((value) => parseExpiry(value) !== null, {
			message: invalidCardDetailsMessage,
		}),
	});

type SpreedlyFormData = z.infer<ReturnType<typeof createSpreedlySchema>>;

type SpreedlyFormProps = {
	paymentSession: NonNullable<RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"]>;
	formId: string;
	onError: (error: PaymentFormError) => void;
	onSuccess: (publicIdentifier: string) => void;
	setIsProcessing: (isProcessing: boolean) => void;
};

export const SpreedlyForm = ({ paymentSession, formId, onSuccess, onError, setIsProcessing }: SpreedlyFormProps) => {
	const { t } = useLingui();

	const { mutate: acknowledgeFailedPayment } = offstreetClient.paymentSession.acknowledgeFailedPayment.useMutation();
	const recover = (attempt: PaymentAttempt, publicIdentifier?: string) => {
		const { outcome, shouldAcknowledge } = decidePaymentRecovery(attempt);
		if (outcome === "succeeded") {
			onSuccess(publicIdentifier ?? "");
		} else {
			if (shouldAcknowledge) {
				acknowledgeFailedPayment({ paymentId: paymentSession.paymentId });
			}
			onError(outcome);
		}
		setIsProcessing(false);
	};
	const { mutate: completePaymentSession } = offstreetClient.paymentSession.completePaymentSession.useMutation({
		onSuccess: (data) =>
			recover(
				{ type: "completed", status: data.status },
				data.status === "succeeded" ? data.publicIdentifier : undefined,
			),
		onError: () => recover({ type: "request-failed" }),
	});

	const { isReady, submit } = useSpreedly({
		authDetails: paymentSession.client?.spreedly,
		numberContainerId: NUMBER_CONTAINER_ID,
		cvvContainerId: CVV_CONTAINER_ID,
		callbacks: {
			onToken: (token) => {
				completePaymentSession({ paymentSessionId: paymentSession.paymentId, token });
			},
			onError: () => recover({ type: "not-charged" }),
		},
	});

	return (
		<Form
			id={formId}
			className="space-y-4"
			schema={createSpreedlySchema({ invalidCardDetailsMessage: t`Enter a valid expiry date` })}
			initialValues={{ firstName: "", lastName: "", expiry: "" }}
			onSubmit={(values: SpreedlyFormData) => {
				const expiry = parseExpiry(values.expiry);
				// The Spreedly SDK must be initialized and the expiry valid before we can tokenize.
				if (!isReady || !expiry) {
					return;
				}
				setIsProcessing(true);
				submit({
					first_name: values.firstName,
					last_name: values.lastName,
					month: expiry.month,
					year: expiry.year,
				});
			}}
		>
			<div className="grid grid-cols-2 gap-4">
				<FormInput name="firstName" label={t`First name`} autoComplete="cc-given-name" isRequired />
				<FormInput name="lastName" label={t`Last name`} autoComplete="cc-family-name" isRequired />
			</div>

			<FormControl name={NUMBER_CONTAINER_ID} label={t`Card number`} isRequired>
				{/* Spreedly mounts a secure card-number iframe (cross-origin) into this fixed-height container. */}
				<div id={NUMBER_CONTAINER_ID} role="group" aria-label={t`Card number`} className={HOSTED_FIELD_CLASS} />
			</FormControl>

			<div className="grid grid-cols-2 gap-4">
				<FormInput
					name="expiry"
					label={t`Expiry`}
					placeholder={t`MM/YY`}
					inputMode="numeric"
					maxLength={5}
					autoComplete="cc-exp"
					transform={formatExpiry}
					isRequired
				/>
				<FormControl name={CVV_CONTAINER_ID} label={t`CVV`} isRequired>
					{/* Spreedly mounts a secure CVV iframe into this fixed-height container. */}
					<div id={CVV_CONTAINER_ID} role="group" aria-label={t`CVV`} className={HOSTED_FIELD_CLASS} />
				</FormControl>
			</div>
		</Form>
	);
};
